import { FC } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { useAccountSummary } from './hooks';
import { PatientInfoSection, ServiceInfoSection, FinancialSummarySection } from './components';

export interface AccountSummaryDialogProps {
  open: boolean;
  accountId: string | null;
  onClose: () => void;
}

export const AccountSummaryDialog: FC<AccountSummaryDialogProps> = ({
  open,
  accountId,
  onClose,
}) => {
  const {
    account,
    isLoading,
    error,
    isProcessing,
    isCreating,
    errorMessage,
    handleCreateWriteOff,
    handleStillCollectible,
    handleCloseError,
  } = useAccountSummary({ accountId, open, onClose });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Account Summary</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error">Failed to load account details. Please try again.</Alert>
          )}

          {account && !isLoading && (
            <>
              <PatientInfoSection account={account} />
              <ServiceInfoSection account={account} />
              <FinancialSummarySection account={account} />
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading || isProcessing || isCreating}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateWriteOff}
          variant="contained"
          disabled={isLoading || !account || isProcessing || isCreating}
          startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isCreating ? 'Creating...' : 'Create Write-Off'}
        </Button>
        <Button
          onClick={handleStillCollectible}
          variant="contained"
          color="secondary"
          disabled={isLoading || !account || isProcessing || isCreating}
        >
          Still Collectible
        </Button>
      </DialogActions>

      {/* Error notification */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AccountSummaryDialog;
